import React, { useState, useMemo } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  ArrowLeft, Plus, Trash2, Zap, Clock, Award, Layers,
  CheckCircle2, AlertCircle, Sparkles, HelpCircle, X, Trophy
} from 'lucide-react'

interface ExamInfo {
  id: number
  name: string
  code: string
  category?: string
}

interface MarkingRuleOption {
  id: number
  name: string
  question_type: string
  scoring_strategy: string
  scoring_params?: Record<string, any>
}

interface SubjectOption {
  id: number
  name: string
  code: string
}

interface SyllabusOption {
  id: number
  name: string
}

interface SectionData {
  id?: number
  name: string
  sort_order: number
  total_questions: number
  max_attempts?: number | null
  duration_minutes?: number | null
  subject_hint?: string | null
  exam_marking_rule_id?: number | null
  subject_id?: number | null
  syllabus_id?: number | null
  subject_ids?: number[]
  chapter_ids?: number[]
  scope_deferred: boolean
  deferred_scope_type?: 'chapter' | 'subject' | null
  _destroy?: boolean
}

interface BlueprintData {
  id?: number
  name: string
  test_type_key: string
  total_questions: number
  duration_minutes?: number | null
  timer_type?: string
  positive_marks: number
  negative_marks: number
  leaderboard_eligible?: boolean
  exam_marking_rule_id?: number | null
  max_attempts?: number | null
  description?: string
  instructions_html?: string
  sort_order?: number
  sections?: SectionData[]
}

interface Props {
  exam: ExamInfo
  blueprint?: BlueprintData | null
  marking_rules: MarkingRuleOption[]
  subjects: SubjectOption[]
  syllabi: SyllabusOption[]
}

export default function BlueprintEditor({
  exam,
  blueprint,
  marking_rules = [],
  subjects = [],
  syllabi = []
}: Props) {
  const isEditing = Boolean(blueprint?.id)

  // Top level fields
  const [name, setName] = useState(blueprint?.name || '')
  const [key, setKey] = useState(blueprint?.test_type_key || '')
  const isUntimedInit = blueprint?.timer_type === 'none' || (blueprint?.duration_minutes == null && blueprint?.timer_type !== 'sectional')
  const [timerType, setTimerType] = useState<'global' | 'sectional' | 'none'>(
    isUntimedInit ? 'none' : (blueprint?.timer_type === 'sectional' ? 'sectional' : 'global')
  )
  const [duration, setDuration] = useState<string | number>(
    isUntimedInit ? '' : (blueprint?.duration_minutes ?? 180)
  )
  const [totalQuestions, setTotalQuestions] = useState<number>(blueprint?.total_questions ?? 100)

  const isOptionalInit = Boolean(
    blueprint?.max_attempts && blueprint.max_attempts > 0 && blueprint.max_attempts < blueprint.total_questions
  )
  const [allMandatory, setAllMandatory] = useState<boolean>(!isOptionalInit)
  const [maxAttempts, setMaxAttempts] = useState<string | number>(isOptionalInit ? blueprint!.max_attempts! : '')

  const isSectionOptionalInit = Boolean(
    blueprint?.sections?.some(s => s.max_attempts != null && Number(s.max_attempts) > 0)
  )
  const [allowSectionOptionalChoice, setAllowSectionOptionalChoice] = useState<boolean>(isSectionOptionalInit)
  const [allowSectionMarking, setAllowSectionMarking] = useState<boolean>(() => {
    return Boolean(blueprint?.sections?.some(s => s.exam_marking_rule_id != null))
  })
  const [autoCalcDuration, setAutoCalcDuration] = useState<boolean>(() => {
    if (blueprint?.timer_type === 'sectional') return true
    const secSum = (blueprint?.sections || []).reduce((acc, s) => acc + (Number(s.duration_minutes) || 0), 0)
    return secSum > 0 && secSum === blueprint?.duration_minutes
  })

  const [posMarks, setPosMarks] = useState<number>(blueprint?.positive_marks ?? 1.0)
  const [negMarks, setNegMarks] = useState<number>(blueprint?.negative_marks ?? 0.25)
  const [leaderboardEligible, setLeaderboardEligible] = useState<boolean>(blueprint?.leaderboard_eligible ?? false)
  const [markingRuleId, setMarkingRuleId] = useState<number | ''>(blueprint?.exam_marking_rule_id || '')
  const [description, setDescription] = useState<string>(blueprint?.description || '')
  const [instructions, setInstructions] = useState<string>(blueprint?.instructions_html || '')
  const [sortOrder, setSortOrder] = useState<number>(blueprint?.sort_order ?? 0)

  // Sections
  const [sections, setSections] = useState<SectionData[]>(() => {
    if (blueprint?.sections && blueprint.sections.length > 0) {
      return blueprint.sections.map((s, idx) => ({
        ...s,
        sort_order: s.sort_order ?? idx,
        subject_ids: (s.subject_ids && s.subject_ids.length > 0)
          ? s.subject_ids
          : (s.subject_id ? [s.subject_id] : [])
      }))
    }
    return [
      {
        name: 'Section 1',
        sort_order: 0,
        total_questions: 30,
        scope_deferred: false,
        deferred_scope_type: null,
        subject_ids: [],
        chapter_ids: []
      }
    ]
  })

  const [submitting, setSubmitting] = useState<boolean>(false)

  // Active sections (not destroyed)
  const activeSections = useMemo(() => sections.filter(s => !s._destroy), [sections])

  // Auto-calculated sum of section questions
  const sectionQuestionsSum = useMemo(() => {
    return activeSections.reduce((acc, s) => acc + (Number(s.total_questions) || 0), 0)
  }, [activeSections])

  // Total questions effective
  const effectiveTotalQuestions = sectionQuestionsSum > 0 ? sectionQuestionsSum : totalQuestions

  // Auto-calculated duration if sectional or combined from sections
  const sectionDurationSum = useMemo(() => {
    return activeSections.reduce((acc, s) => acc + (Number(s.duration_minutes) || 0), 0)
  }, [activeSections])

  const effectiveDurationMinutes = useMemo(() => {
    if (timerType === 'none') return null
    if (timerType === 'sectional') return sectionDurationSum
    if (autoCalcDuration && sectionDurationSum > 0) return sectionDurationSum
    return Number(duration) || null
  }, [timerType, duration, sectionDurationSum, autoCalcDuration])

  // Auto-calculated estimated total marks
  const estimatedTotalMarks = useMemo(() => {
    let total = 0
    activeSections.forEach(s => {
      const qCount = Number(s.total_questions) || 0
      let markPerQ = posMarks
      if (s.exam_marking_rule_id) {
        const rule = marking_rules.find(r => r.id === s.exam_marking_rule_id)
        if (rule?.scoring_params?.correct != null) {
          markPerQ = Number(rule.scoring_params.correct)
        }
      }
      total += qCount * markPerQ
    })
    return total
  }, [activeSections, posMarks, marking_rules])

  // Handlers
  const handleAddSection = () => {
    setSections(prev => [
      ...prev,
      {
        name: `Section ${prev.filter(s => !s._destroy).length + 1}`,
        sort_order: prev.length,
        total_questions: 25,
        scope_deferred: false,
        deferred_scope_type: null,
        subject_ids: [],
        chapter_ids: []
      }
    ])
  }

  const handleUpdateSection = (index: number, updates: Partial<SectionData>) => {
    setSections(prev => {
      const next = [...prev]
      next[index] = { ...next[index], ...updates }
      return next
    })
  }

  const handleRemoveSection = (index: number) => {
    setSections(prev => {
      const target = prev[index]
      if (target.id) {
        const next = [...prev]
        next[index] = { ...target, _destroy: true }
        return next
      }
      return prev.filter((_, idx) => idx !== index)
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    if (activeSections.length === 0) {
      alert('Please configure at least one paper section.')
      return
    }

    setSubmitting(true)

    const parsedMaxAttempts = !allMandatory && maxAttempts !== '' && Number(maxAttempts) > 0 && Number(maxAttempts) < effectiveTotalQuestions
      ? Number(maxAttempts)
      : null

    const formData = {
      exam_test_type: {
        name: name.trim(),
        test_type_key: key.trim() || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
        timer_type: timerType,
        duration_minutes: timerType === 'none' ? null : effectiveDurationMinutes,
        total_questions: effectiveTotalQuestions,
        max_attempts: parsedMaxAttempts,
        positive_marks: posMarks,
        negative_marks: negMarks,
        leaderboard_eligible: leaderboardEligible,
        exam_marking_rule_id: markingRuleId || null,
        description: description.trim(),
        instructions_html: instructions.trim() || null,
        sort_order: sortOrder,
        exam_test_type_sections_attributes: sections.map((s, i) => ({
          ...(s.id ? { id: s.id } : {}),
          name: s.name.trim(),
          sort_order: i,
          total_questions: Number(s.total_questions) || 0,
          max_attempts: (s.max_attempts && Number(s.max_attempts) > 0 && Number(s.max_attempts) < (Number(s.total_questions) || 0))
            ? Number(s.max_attempts)
            : null,
          duration_minutes: s.duration_minutes ? Number(s.duration_minutes) : null,
          subject_hint: s.subject_hint || null,
          exam_marking_rule_id: s.exam_marking_rule_id || null,
          subject_id: (s.subject_ids && s.subject_ids.length > 0) ? s.subject_ids[0] : (s.subject_id || null),
          syllabus_id: s.syllabus_id || null,
          subject_ids: (s.subject_ids && s.subject_ids.length > 0) ? s.subject_ids : (s.subject_id ? [s.subject_id] : []),
          chapter_ids: s.chapter_ids || [],
          scope_deferred: Boolean(s.scope_deferred),
          deferred_scope_type: s.scope_deferred ? (s.deferred_scope_type || 'subject') : null,
          ...(s._destroy ? { _destroy: '1' } : {})
        }))
      }
    }

    if (isEditing && blueprint?.id) {
      router.patch(`/admin/exams/${exam.id}/exam_test_types/${blueprint.id}`, formData, {
        onFinish: () => setSubmitting(false)
      })
    } else {
      router.post(`/admin/exams/${exam.id}/exam_test_types`, formData, {
        onFinish: () => setSubmitting(false)
      })
    }
  }

  return (
    <AppShell title={`${isEditing ? 'Edit' : 'New'} Blueprint - ${exam.name}`}>
      <div className="space-y-6 max-w-7xl mx-auto pb-12">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-4">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Link
                href={`/admin/exams/${exam.id}?tab=blueprints`}
                className="hover:text-foreground transition-colors inline-flex items-center gap-1 font-medium group"
              >
                <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
                <span className="sm:hidden">Blueprints</span>
                <span className="hidden sm:inline">Back to {exam.name} Blueprints</span>
              </Link>
            </div>
            <h1 className="text-base sm:text-xl font-bold text-foreground flex items-start sm:items-center gap-2">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
              <span className="leading-snug">
                {isEditing ? `Edit Blueprint: ${blueprint?.name}` : `Create Test Blueprint for ${exam.name}`}
              </span>
            </h1>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Configure paper structure, timings, marking rules, and subject scopes.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1 sm:pt-0 shrink-0">
            <Link
              href={`/admin/exams/${exam.id}?tab=blueprints`}
              className="flex-1 sm:flex-initial px-3.5 py-2 text-xs font-semibold rounded-xl border border-border bg-card hover:bg-muted text-foreground transition-colors text-center"
            >
              Cancel
            </Link>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 sm:flex-initial text-xs font-bold rounded-xl gap-1.5 shadow-sm"
            >
              {submitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Blueprint')}
            </Button>
          </div>
        </div>

        {/* 2-Column Responsive Workspace */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column: Form Cards (8 cols) */}
            <div className="lg:col-span-8 space-y-6">
              {/* Card 1: Blueprint Identity & Timing */}
              <Card className="border-border bg-card shadow-glass-xs rounded-2xl">
                <CardHeader className="p-4 sm:p-5 pb-3 border-b border-border/60">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <Award className="h-4 w-4 text-primary" /> Blueprint Identity & Parameters
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Define test naming, pattern identifier, and time limit structure.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-foreground">Blueprint Name *</Label>
                      <Input
                        value={name}
                        onChange={(e) => {
                          const val = e.target.value
                          setName(val)
                          if (!isEditing) {
                            const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
                            setKey(slug)
                          }
                        }}
                        placeholder="e.g. SBI PO Prelims Full Mock"
                        required
                        className="h-9 text-xs"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-foreground">Key Identifier (Slug) *</Label>
                      <Input
                        value={key}
                        onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                        placeholder="e.g. full_mock, speed_test"
                        required
                        className="h-9 text-xs font-mono"
                      />
                    </div>
                  </div>

                  {/* Timing Mode & Duration */}
                  <div className="p-3.5 bg-muted/30 border border-border/80 rounded-xl space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-primary" /> Timing Mode
                      </Label>
                      <div className="grid grid-cols-3 gap-1 p-1 bg-muted/50 rounded-xl border border-border/60 text-xs w-full sm:w-auto">
                        <button
                          type="button"
                          onClick={() => setTimerType('global')}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all text-center ${
                            timerType === 'global'
                              ? 'bg-card text-foreground shadow-xs border border-border'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Single Global
                        </button>
                        <button
                          type="button"
                          onClick={() => setTimerType('sectional')}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all text-center ${
                            timerType === 'sectional'
                              ? 'bg-card text-foreground shadow-xs border border-border'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Sectional
                        </button>
                        <button
                          type="button"
                          onClick={() => setTimerType('none')}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all text-center ${
                            timerType === 'none'
                              ? 'bg-card text-foreground shadow-xs border border-border'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Untimed
                        </button>
                      </div>
                    </div>

                    {timerType === 'global' && (
                      <div className="pt-2 border-t border-border/50 space-y-2.5">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-[11px] font-medium text-foreground">Duration (Minutes) *</Label>
                            {sectionDurationSum > 0 && autoCalcDuration && (
                              <button
                                type="button"
                                onClick={() => {
                                  setAutoCalcDuration(false)
                                  setDuration(sectionDurationSum)
                                }}
                                className="text-[11px] font-medium text-primary hover:underline"
                              >
                                Edit manually
                              </button>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <div className="w-full sm:w-40 shrink-0">
                              <Input
                                type="number"
                                min="1"
                                value={autoCalcDuration && sectionDurationSum > 0 ? sectionDurationSum : duration}
                                disabled={autoCalcDuration && sectionDurationSum > 0}
                                onChange={(e) => {
                                  setAutoCalcDuration(false)
                                  setDuration(e.target.value ? Number(e.target.value) : '')
                                }}
                                placeholder="e.g. 180"
                                required
                                className="h-9 text-xs font-bold w-full"
                              />
                            </div>

                            {sectionDurationSum > 0 && (
                              <Button
                                type="button"
                                variant={autoCalcDuration ? 'secondary' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  const nextAuto = !autoCalcDuration
                                  setAutoCalcDuration(nextAuto)
                                  if (nextAuto) {
                                    setDuration(sectionDurationSum)
                                  }
                                }}
                                className={`h-9 text-xs font-semibold gap-1.5 rounded-xl w-full sm:w-auto shrink-0 justify-center border ${
                                  autoCalcDuration
                                    ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                                    : 'border-border'
                                }`}
                              >
                                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                                <span>{autoCalcDuration ? `Auto-synced (${sectionDurationSum}m)` : `Sync with sections (${sectionDurationSum}m)`}</span>
                              </Button>
                            )}
                          </div>
                        </div>

                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          {autoCalcDuration && sectionDurationSum > 0
                            ? `Auto-synced with total sectional durations (${sectionDurationSum} mins). Candidates have free navigation between sections.`
                            : 'Candidates have this total duration across the entire test with free navigation between sections.'}
                        </p>
                      </div>
                    )}

                    {timerType === 'sectional' && (
                      <div className="pt-2 border-t border-border/50 space-y-2">
                        <div className="flex items-center gap-4">
                          <div className="w-56">
                            <Label className="text-[11px] font-medium text-foreground">Total Combined Duration</Label>
                            <div className="h-9 px-3 rounded-lg border border-border bg-muted/40 flex items-center justify-between font-mono font-bold text-foreground text-xs">
                              <span>{sectionDurationSum} mins</span>
                              <Badge variant="outline" className="text-[10px] font-sans font-bold text-primary bg-primary/10 border-primary/20">
                                Combined Sum
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Each section has its own individual timer. Total duration is automatically combined from all sectional timers ({sectionDurationSum} mins across {activeSections.length} sections).
                        </p>
                      </div>
                    )}

                    {timerType === 'none' && (
                      <p className="text-[11px] text-muted-foreground pt-1">
                        Untimed mode suitable for study drills, daily quizzes, or open practice papers.
                      </p>
                    )}
                  </div>

                  {/* Marking Scheme Preset & Values */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-foreground">Marking Scheme Preset</Label>
                        <select
                          value={markingRuleId !== '' ? String(markingRuleId) : ''}
                          onChange={(e) => {
                            const ruleId = e.target.value ? Number(e.target.value) : ''
                            setMarkingRuleId(ruleId)
                            if (ruleId) {
                              const matched = marking_rules.find(r => r.id === ruleId)
                              if (matched) {
                                const correctVal = matched.scoring_params?.correct ?? matched.scoring_params?.full_correct ?? 1.0
                                const wrongVal = matched.scoring_params?.wrong ?? matched.scoring_params?.any_wrong ?? (matched.scoring_strategy === 'no_penalty' ? 0 : -0.25)
                                setPosMarks(Number(correctVal) || 1.0)
                                setNegMarks(Math.abs(Number(wrongVal) || 0))
                              }
                            }
                          }}
                          className="w-full h-9 px-2.5 rounded-lg border border-border bg-card text-xs font-medium text-foreground"
                        >
                          <option value="">-- Custom / Manual Values --</option>
                          {marking_rules.map(r => (
                            <option key={r.id} value={String(r.id)}>
                              {r.name} ({r.question_type})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-foreground">Positive Marks (+)</Label>
                        <Input
                          type="number"
                          step="0.25"
                          min="0.25"
                          required
                          value={posMarks}
                          onChange={(e) => setPosMarks(Number(e.target.value) || 1)}
                          className="h-9 text-xs font-bold text-primary"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-foreground">Penalty Marks (-)</Label>
                        <Input
                          type="number"
                          step="0.05"
                          min="0"
                          value={negMarks}
                          onChange={(e) => setNegMarks(Number(e.target.value) || 0)}
                          className="h-9 text-xs font-bold text-destructive"
                        />
                      </div>
                    </div>

                    {/* Section-level Marking Override Global Toggle */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/20 border border-border/60">
                      <div className="space-y-0.5">
                        <Label htmlFor="bp-allow-section-marking" className="text-xs font-semibold text-foreground cursor-pointer">
                          Allow custom marking schemes per section
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                          {allowSectionMarking
                            ? 'Enabled: You can configure custom marking schemes on individual sections below.'
                            : 'Disabled: All sections inherit the blueprint marking scheme (+1 / -0.25). Section builder stays clean.'}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="bp-allow-section-marking"
                        checked={allowSectionMarking}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setAllowSectionMarking(checked)
                          if (!checked) {
                            setSections(prev => prev.map(s => ({ ...s, exam_marking_rule_id: null })))
                          }
                        }}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                      />
                    </div>
                  </div>

                  {/* Compulsory vs Optional Questions */}
                  <div className="p-3 bg-muted/30 border border-border/80 rounded-xl space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 pr-2">
                        <Label
                          htmlFor="bp-all-mandatory"
                          className="text-xs font-semibold text-foreground cursor-pointer flex items-center gap-1.5"
                        >
                          All questions are mandatory (compulsory)
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                          {allMandatory
                            ? 'Candidates must attempt all questions across the blueprint. Sections have no attempt limits.'
                            : 'Candidates have choice: configure a blueprint-wide or section-specific attempt limit.'}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="bp-all-mandatory"
                        checked={allMandatory}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setAllMandatory(checked)
                          if (checked) {
                            setMaxAttempts('')
                            setAllowSectionOptionalChoice(false)
                            setSections(prev => prev.map(s => ({ ...s, max_attempts: null })))
                          }
                        }}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                      />
                    </div>

                    {!allMandatory && (
                      <div className="pt-2 border-t border-border/50 space-y-2.5">
                        <div className="flex items-center gap-4 text-xs">
                          <span className="font-semibold text-foreground text-xs">Choice Scope:</span>
                          <label className="flex items-center gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground">
                            <input
                              type="radio"
                              name="bp-attempt-scope"
                              checked={!allowSectionOptionalChoice}
                              onChange={() => {
                                setAllowSectionOptionalChoice(false)
                                setSections(prev => prev.map(s => ({ ...s, max_attempts: null })))
                              }}
                              className="text-primary focus:ring-primary"
                            />
                            Blueprint-Wide Limit
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground">
                            <input
                              type="radio"
                              name="bp-attempt-scope"
                              checked={allowSectionOptionalChoice}
                              onChange={() => {
                                setAllowSectionOptionalChoice(true)
                                setMaxAttempts('')
                              }}
                              className="text-primary focus:ring-primary"
                            />
                            Section-Specific Limits
                          </label>
                        </div>

                        {!allowSectionOptionalChoice ? (
                          <div className="flex items-center gap-3">
                            <div className="w-48">
                              <Label className="text-[11px] font-medium text-foreground">Max Questions to Attempt *</Label>
                              <Input
                                type="number"
                                min="1"
                                max={effectiveTotalQuestions > 1 ? effectiveTotalQuestions - 1 : 1}
                                value={maxAttempts}
                                onChange={(e) => setMaxAttempts(e.target.value ? Number(e.target.value) : '')}
                                placeholder={`e.g. ${Math.max(1, effectiveTotalQuestions - 10)}`}
                                required
                                className="h-9 text-xs font-bold"
                              />
                            </div>
                            <span className="text-[11px] text-muted-foreground pt-4">
                              Must be less than total questions ({effectiveTotalQuestions})
                            </span>
                          </div>
                        ) : (
                          <p className="text-[11px] text-primary font-medium">
                            Section-specific optional choices enabled. Configure max attempts on each section below.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Description & Candidate Note */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-foreground">Description</Label>
                      <textarea
                        rows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Brief summary describing what this test covers..."
                        className="w-full p-2.5 rounded-lg border border-border bg-card text-xs font-medium text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <p className="text-[11px] text-muted-foreground leading-tight">
                        Shown as the test subtitle on student mock test cards, search catalogs, and admin blueprint overviews.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-foreground">Candidate Instructions / Note</Label>
                      <textarea
                        rows={2}
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        placeholder="e.g. 'Read all questions carefully. Negative marking of 0.25 applies.'"
                        className="w-full p-2.5 rounded-lg border border-border bg-card text-xs font-medium text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <p className="text-[11px] text-muted-foreground leading-tight">
                        Displayed in a dedicated card on the Pre-Test Instructions page and inside the live exam room toolbar modal.
                      </p>
                    </div>
                  </div>

                  {/* Leaderboard Eligibility Setting */}
                  <div className="p-3.5 rounded-xl border border-border/80 bg-muted/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 pr-2">
                        <Label
                          htmlFor="bp-leaderboard-eligible"
                          className="text-xs font-bold text-foreground cursor-pointer flex items-center gap-1.5"
                        >
                          <Trophy className="h-3.5 w-3.5 text-primary" /> Include in Nationwide Leaderboard & Standings
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                          When enabled, student attempts on tests generated from this blueprint will update candidate nationwide rankings and appear on the exam leaderboard.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="bp-leaderboard-eligible"
                        checked={leaderboardEligible}
                        onChange={(e) => setLeaderboardEligible(e.target.checked)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Paper Sections Builder */}
              <Card className="border-border bg-card shadow-glass-xs rounded-2xl">
                <CardHeader className="p-4 sm:p-5 pb-3 border-b border-border/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                      <Layers className="h-4 w-4 text-primary shrink-0" />
                      <span>Paper Sections Builder ({activeSections.length})</span>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Define each section in this exam paper with its question budget, timing, and scope rule.
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddSection}
                    className="gap-1.5 text-xs font-bold rounded-xl w-full sm:w-auto shrink-0 shadow-xs"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Section
                  </Button>
                </CardHeader>
                <CardContent className="p-4 sm:p-5 space-y-4">
                  {activeSections.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-border rounded-xl space-y-2">
                      <p className="text-xs text-muted-foreground">No sections configured yet.</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddSection}
                        className="text-xs font-bold"
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add First Section
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sections.map((sec, idx) => {
                        if (sec._destroy) return null
                        return (
                          <div
                            key={sec.id || idx}
                            className="p-3.5 sm:p-4 rounded-xl border border-border bg-card shadow-glass-xs space-y-3.5 transition-all hover:border-border/80"
                          >
                            {/* Row 1: Section Header & Inputs */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-extrabold shrink-0">
                                    {idx + 1}
                                  </span>
                                  <span className="text-xs font-bold text-foreground truncate">
                                    {sec.name ? sec.name : `Section ${idx + 1}`}
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                                  onClick={() => handleRemoveSection(idx)}
                                  title="Remove section"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
                                <div className={`${timerType !== 'none' ? 'sm:col-span-6' : 'sm:col-span-8'} space-y-1`}>
                                  <Label className="text-xs text-foreground font-semibold block">Section Name *</Label>
                                  <Input
                                    value={sec.name}
                                    onChange={(e) => handleUpdateSection(idx, { name: e.target.value })}
                                    placeholder="e.g. Reasoning Ability"
                                    required
                                    className="h-9 text-xs font-semibold"
                                  />
                                </div>

                                <div className={`grid ${timerType !== 'none' ? 'grid-cols-2 sm:col-span-6' : 'grid-cols-1 sm:col-span-4'} gap-2`}>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-foreground font-semibold block text-center sm:text-left">Questions *</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={sec.total_questions || ''}
                                      onChange={(e) => handleUpdateSection(idx, { total_questions: Number(e.target.value) || 0 })}
                                      placeholder="Count"
                                      required
                                      className="h-9 text-xs text-center font-bold"
                                    />
                                  </div>

                                  {timerType !== 'none' && (
                                    <div className="space-y-1">
                                      <Label className="text-xs text-foreground font-semibold block text-center sm:text-left truncate">
                                        {timerType === 'sectional' ? 'Timer (Mins) *' : 'Timer (Mins)'}
                                      </Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={sec.duration_minutes || ''}
                                        onChange={(e) => handleUpdateSection(idx, { duration_minutes: e.target.value ? Number(e.target.value) : null })}
                                        placeholder={timerType === 'sectional' ? 'Minutes' : 'Optional'}
                                        required={timerType === 'sectional'}
                                        className="h-9 text-xs text-center font-bold text-primary"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Row 2: Optional attempt limit & section marking override (Only shown if enabled at global level) */}
                            {(allowSectionOptionalChoice || allowSectionMarking) && (
                              <div className={`grid gap-3 pt-2 border-t border-border/40 ${allowSectionOptionalChoice && allowSectionMarking ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                                {allowSectionOptionalChoice && (
                                  <div className="p-2.5 rounded-xl bg-muted/20 border border-border/50 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <label
                                        htmlFor={`sec-opt-${idx}`}
                                        className="text-xs font-semibold text-foreground cursor-pointer flex items-center gap-1.5"
                                      >
                                        Optional Choice (Attempt Limit)
                                      </label>
                                      <input
                                        type="checkbox"
                                        id={`sec-opt-${idx}`}
                                        checked={Boolean(sec.max_attempts && Number(sec.max_attempts) > 0)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            const total = Number(sec.total_questions) || 20
                                            handleUpdateSection(idx, { max_attempts: Math.max(1, total - 5) })
                                          } else {
                                            handleUpdateSection(idx, { max_attempts: null })
                                          }
                                        }}
                                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                                      />
                                    </div>
                                    {Boolean(sec.max_attempts && Number(sec.max_attempts) > 0) ? (
                                      <div className="space-y-1 pt-1.5 border-t border-border/40">
                                        <div className="flex items-center justify-between">
                                          <Label className="text-[11px] font-medium text-foreground">
                                            Max Questions to Attempt *
                                          </Label>
                                          <span className="text-[10px] text-muted-foreground">
                                            out of {sec.total_questions || 0} Qs
                                          </span>
                                        </div>
                                        <Input
                                          type="number"
                                          min="1"
                                          max={(Number(sec.total_questions) || 1) > 1 ? (Number(sec.total_questions) || 1) - 1 : 1}
                                          value={sec.max_attempts || ''}
                                          onChange={(e) => handleUpdateSection(idx, { max_attempts: e.target.value ? Number(e.target.value) : null })}
                                          placeholder={`e.g. ${Math.max(1, (Number(sec.total_questions) || 15) - 5)}`}
                                          className="h-8 text-xs font-bold"
                                        />
                                      </div>
                                    ) : (
                                      <p className="text-[11px] text-muted-foreground">
                                        All {sec.total_questions || 0} questions are compulsory.
                                      </p>
                                    )}
                                  </div>
                                )}

                                {allowSectionMarking && (
                                  <div className="p-2.5 rounded-xl bg-muted/20 border border-border/50 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <label
                                        htmlFor={`sec-marking-${idx}`}
                                        className="text-xs font-semibold text-foreground cursor-pointer flex items-center gap-1.5"
                                      >
                                        Custom Section Marking
                                      </label>
                                      <input
                                        type="checkbox"
                                        id={`sec-marking-${idx}`}
                                        checked={Boolean(sec.exam_marking_rule_id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            handleUpdateSection(idx, { exam_marking_rule_id: marking_rules[0]?.id || null })
                                          } else {
                                            handleUpdateSection(idx, { exam_marking_rule_id: null })
                                          }
                                        }}
                                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                                      />
                                    </div>
                                    {Boolean(sec.exam_marking_rule_id) ? (
                                      <div className="space-y-1 pt-1.5 border-t border-border/40">
                                        <Label className="text-[11px] font-medium text-foreground">
                                          Select Marking Scheme *
                                        </Label>
                                        <select
                                          value={sec.exam_marking_rule_id ? String(sec.exam_marking_rule_id) : ''}
                                          onChange={(e) => handleUpdateSection(idx, { exam_marking_rule_id: e.target.value ? Number(e.target.value) : null })}
                                          className="w-full h-8 px-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                                        >
                                          {marking_rules.map(r => (
                                            <option key={r.id} value={String(r.id)}>
                                              {r.name} ({r.scoring_strategy})
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    ) : (
                                      <p className="text-[11px] text-muted-foreground">
                                        Inherits blueprint marking (+{posMarks} / -{negMarks}).
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Row 3: Scope Resolution (Fixed vs Deferred) */}
                            <div className="p-3 sm:p-3.5 rounded-xl bg-muted/40 border border-border/60 space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                  <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" /> Scope Configuration
                                </Label>
                                <div className="grid grid-cols-2 gap-1 p-1 bg-muted/50 rounded-xl border border-border/60 text-xs w-full sm:w-auto">
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateSection(idx, { scope_deferred: false, deferred_scope_type: null })}
                                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all text-center ${
                                      !sec.scope_deferred
                                        ? 'bg-card text-foreground shadow-xs border border-border'
                                        : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                  >
                                    Fixed Scope
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateSection(idx, { scope_deferred: true, deferred_scope_type: sec.deferred_scope_type || 'subject' })}
                                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all text-center ${
                                      Boolean(sec.scope_deferred)
                                        ? 'bg-card text-foreground shadow-xs border border-border'
                                        : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                  >
                                    Deferred Scope
                                  </button>
                                </div>
                              </div>

                              {sec.scope_deferred ? (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
                                  <span className="text-xs text-muted-foreground shrink-0">Admin picks at generation:</span>
                                  <select
                                    value={sec.deferred_scope_type || 'subject'}
                                    onChange={(e) => handleUpdateSection(idx, { deferred_scope_type: e.target.value as 'chapter' | 'subject' })}
                                    className="w-full sm:w-auto h-9 px-3 text-xs font-semibold rounded-lg border border-border bg-card text-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                  >
                                    <option value="subject">Subject Focus (User picks Subject)</option>
                                    <option value="chapter">Chapter Focus (User picks Subject & Chapter)</option>
                                  </select>
                                </div>
                              ) : (() => {
                                const secSubIds: number[] = (sec.subject_ids && sec.subject_ids.length > 0)
                                  ? sec.subject_ids
                                  : (sec.subject_id ? [sec.subject_id] : [])
                                const assignedSubs = subjects.filter(s => secSubIds.includes(s.id))
                                const unassignedSubs = subjects.filter(s => !secSubIds.includes(s.id))

                                const handleAddSub = (subId: number) => {
                                  if (!secSubIds.includes(subId)) {
                                    const nextSubs = [...secSubIds, subId]
                                    const nextNames = nextSubs
                                      .map(id => subjects.find(s => s.id === id)?.name)
                                      .filter(Boolean)
                                    const autoHint = (!sec.subject_hint || subjects.some(s => s.name === sec.subject_hint))
                                      ? nextNames.join(' & ')
                                      : sec.subject_hint
                                    handleUpdateSection(idx, {
                                      subject_ids: nextSubs,
                                      subject_id: nextSubs[0],
                                      subject_hint: autoHint,
                                      chapter_ids: []
                                    })
                                  }
                                }

                                const handleRemoveSub = (subId: number) => {
                                  const nextSubs = secSubIds.filter(id => id !== subId)
                                  const nextNames = nextSubs
                                    .map(id => subjects.find(s => s.id === id)?.name)
                                    .filter(Boolean)
                                  handleUpdateSection(idx, {
                                    subject_ids: nextSubs,
                                    subject_id: nextSubs[0] || null,
                                    subject_hint: nextNames.length > 0 ? nextNames.join(' & ') : '',
                                    chapter_ids: []
                                  })
                                }

                                const handleClearAllSubs = () => {
                                  handleUpdateSection(idx, {
                                    subject_ids: [],
                                    subject_id: null,
                                    subject_hint: '',
                                    chapter_ids: []
                                  })
                                }

                                return (
                                  <div className="space-y-3 pt-1">
                                    <div className="space-y-1.5">
                                      <div className="flex items-center justify-between gap-1">
                                        <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                          Target Subject(s)
                                          {assignedSubs.length > 1 && (
                                            <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-primary/10 text-primary border border-primary/20">
                                              {assignedSubs.length}
                                            </span>
                                          )}
                                        </Label>
                                        {assignedSubs.length > 0 && (
                                          <button
                                            type="button"
                                            onClick={handleClearAllSubs}
                                            className="text-[11px] text-muted-foreground hover:text-destructive transition-colors focus:outline-none"
                                          >
                                            Clear all
                                          </button>
                                        )}
                                      </div>

                                      {/* Assigned Subjects Badges */}
                                      <div className="min-h-[36px] p-2 rounded-lg border border-border bg-muted/20 flex flex-wrap gap-1.5 items-center">
                                        {assignedSubs.length === 0 ? (
                                          <span className="text-xs text-muted-foreground italic px-1">
                                            All / Any Subjects (Entire syllabus)
                                          </span>
                                        ) : (
                                          assignedSubs.map(sub => (
                                            <span
                                              key={sub.id}
                                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                                            >
                                              <span>{sub.name}</span>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveSub(sub.id)}
                                                className="text-primary/70 hover:text-destructive hover:bg-destructive/10 rounded p-0.5 transition-colors focus:outline-none"
                                                title={`Remove ${sub.name}`}
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </span>
                                          ))
                                        )}
                                      </div>

                                      {/* Add Subject Select Dropdown */}
                                      {unassignedSubs.length > 0 && (
                                        <select
                                          value=""
                                          onChange={(e) => {
                                            const subId = Number(e.target.value)
                                            if (subId) handleAddSub(subId)
                                          }}
                                          className="w-full h-9 px-3 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                                        >
                                          <option value="">+ Add subject to this section...</option>
                                          {unassignedSubs.map(sub => (
                                            <option key={sub.id} value={String(sub.id)}>
                                              {sub.name}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>

                                    <div className="space-y-1.5">
                                      <Label className="text-xs font-semibold text-foreground block">
                                        Section Display Title <span className="text-[10px] text-muted-foreground font-normal">(Optional)</span>
                                      </Label>
                                      <Input
                                        value={sec.subject_hint || ''}
                                        onChange={(e) => handleUpdateSection(idx, { subject_hint: e.target.value })}
                                        placeholder="e.g. Reasoning & Computer Aptitude"
                                        className="h-9 text-xs"
                                      />
                                      <p className="text-[11px] text-muted-foreground leading-tight">
                                        Candidate-facing section title shown during the test and on test cards.
                                      </p>
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Sticky Live Summary Card (4 cols) */}
            <div className="lg:col-span-4 sticky top-6 space-y-4">
              <Card className="border-border bg-card shadow-glass-sm rounded-2xl">
                <CardHeader className="pb-3 border-b border-border/60">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary" /> Blueprint Live Summary
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Overview of the configured test structure and scoring.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2.5 rounded-xl bg-muted/40 border border-border/60 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-semibold">Questions</div>
                      <div className="text-lg font-extrabold text-foreground">
                        {effectiveTotalQuestions}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-xl bg-muted/40 border border-border/60 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-semibold">Duration</div>
                      <div className="text-lg font-extrabold text-foreground">
                        {timerType === 'none' ? 'Untimed' : `${effectiveDurationMinutes || 0}m`}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-xl bg-muted/40 border border-border/60 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-semibold">Est. Marks</div>
                      <div className="text-lg font-extrabold text-primary">
                        {estimatedTotalMarks}
                      </div>
                    </div>
                  </div>

                  {/* Section Breakdown Mini List */}
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-foreground">Paper Structure ({activeSections.length} sections)</span>
                      <span className="text-muted-foreground font-mono">{sectionQuestionsSum} Qs</span>
                    </div>

                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                      {activeSections.map((s, idx) => (
                        <div
                          key={s.id || idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/20 border border-border/50 text-xs"
                        >
                          <div className="min-w-0 pr-2">
                            <span className="font-medium text-foreground truncate block">
                              {s.name || `Section ${idx + 1}`}
                            </span>
                            <span className="text-[10px] text-muted-foreground truncate block">
                              {s.scope_deferred
                                ? `Deferred (${s.deferred_scope_type || 'subject'})`
                                : (() => {
                                    const sIds = (s.subject_ids && s.subject_ids.length > 0)
                                      ? s.subject_ids
                                      : (s.subject_id ? [s.subject_id] : [])
                                    if (sIds.length > 0) {
                                      const names = sIds
                                        .map(id => subjects.find(sub => sub.id === id)?.name)
                                        .filter(Boolean)
                                      return names.join(', ') || s.subject_hint || 'General'
                                    }
                                    return s.subject_hint || 'All Subjects'
                                  })()}
                            </span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-bold text-foreground">{s.total_questions} Qs</span>
                            {s.duration_minutes ? (
                              <span className="text-[10px] text-muted-foreground block font-mono">{s.duration_minutes}m</span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-3 border-t border-border/60 space-y-2">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full text-xs font-bold rounded-xl h-10"
                    >
                      {submitting ? 'Saving...' : (isEditing ? 'Save Blueprint Changes' : 'Create Blueprint')}
                    </Button>
                    <Link
                      href={`/admin/exams/${exam.id}?tab=blueprints`}
                      className="w-full h-9 flex items-center justify-center text-xs font-semibold rounded-xl border border-border bg-card hover:bg-muted text-foreground transition-colors"
                    >
                      Discard and Return
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  )
}
